/**
 * Paket-Preisart — bis Buchungsstrecke D4 wortgleich in `index.ts`; herausgezogen, damit
 * `spanne.ts` sie ohne Kreisimport nutzen kann. `index.ts` reicht sie unveraendert weiter.
 */
import type { PriceMode } from "./index";

/**
 * Welchen Betrag traegt ein Paket — und gilt er je Gast oder EINMAL?
 *
 * DER GELDFEHLER, den diese Regel schliesst (07.08.2026, Mandant Storia): Das Paket „Gesamte
 * Location" steht auf `kind = 'flat'` mit 850.000 Cent. Antoine woertlich: *„8.500 EUR sind der
 * Preis fuer die gesamte Location. Das beinhaltet alle Personen."* `packages.kind` wurde nirgends
 * ausgewertet — jeder Anwendungsweg schrieb die Paket-Kopfzeile als `per_person`. Auf 70 Gaeste
 * angewandt ergibt das 595.000,00 EUR statt 8.500,00 EUR.
 *
 * WARUM HIER (dieselbe Begruendung wie bei `ungedeckteNullpreisPositionen`): Ueber diese Frage
 * entscheiden mehrere Stellen — das Anwenden eines Pakets (`apps/api/src/lib/paket-anwenden.ts`),
 * der Kontext fuer das Modell und die Anzeige in `apps/web`. `apps/web` kann nicht aus `apps/api`
 * importieren; zwei Kopien wuerden auseinanderlaufen und die Oberflaeche wuerde „/Gast" an einen
 * Pauschalbetrag schreiben.
 *
 * PREIS-INTEGRITAET: Diese Funktion RECHNET nicht und ERFINDET nichts — sie waehlt nur die Spalte
 * und den Preismodus. Der Betrag kommt aus der Datenbank, multipliziert wird ausschliesslich in
 * `lineGross()`.
 */
export interface PaketPreisQuelle {
  /** `packages.kind`: 'menu' | 'buffet' | 'flat'. */
  kind: string;
  /** `packages.price_per_person_cents` (Brutto-Cent je Gast). */
  pricePerPersonCents: number;
  /** `packages.price_flat_cents` (Brutto-Cent pauschal, Migration 242). */
  priceFlatCents?: number | null;
}

export interface PaketPreisart {
  /** true = EIN Betrag fuer alle Gaeste. */
  pauschal: boolean;
  /** Preismodus der Paket-Kopfzeile — genau so wandert er in `offer_items.price_mode`. */
  priceMode: PriceMode;
  /** Der Betrag aus der Datenbank in Brutto-Cent. 0 = das Paket hat keinen eigenen Preis. */
  betragCents: number;
  /**
   * Der Pauschalbetrag steht (noch) in `price_per_person_cents` statt in `price_flat_cents`.
   *
   * Genau die Lage der Bestandsdaten. Gerechnet wird trotzdem richtig — aber es ist ein Befund,
   * kein Normalzustand: die Zahl steht an einer Spalte, deren Name etwas anderes behauptet.
   * Der Aufrufer meldet das (Selbstwahrnehmung, Antwort des Anwendens), er verweigert es nicht.
   */
  betragAusPersonenSpalte: boolean;
}

export function paketPreisart(p: PaketPreisQuelle): PaketPreisart {
  const proGast = Math.max(0, p.pricePerPersonCents || 0);
  if (p.kind !== "flat") {
    return { pauschal: false, priceMode: "per_person", betragCents: proGast, betragAusPersonenSpalte: false };
  }
  const flat = Math.max(0, p.priceFlatCents ?? 0);
  // Ruecksicht auf den Bestand: Bis Migration 242 gab es nur EINE Preisspalte. Ein Pauschalpaket,
  // dessen Betrag noch dort steht, rechnet ab sofort richtig — ohne dass zuerst Daten umziehen
  // muessen. Umgekehrt gewinnt der ausdrueckliche Pauschalbetrag immer.
  if (flat > 0) return { pauschal: true, priceMode: "flat", betragCents: flat, betragAusPersonenSpalte: false };
  return { pauschal: true, priceMode: "flat", betragCents: proGast, betragAusPersonenSpalte: proGast > 0 };
}

